import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * CardVisualSkeleton — a wallet-card-shaped placeholder (credit-card aspect
 * ratio) standing in for the rendered membership card while it loads: a
 * logo bubble + name lines top-left, a couple of detail lines and a QR
 * square along the bottom. Reused by both the member pass page and the
 * template editor preview.
 */
export function CardVisualSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex aspect-[1.586/1] w-full flex-col justify-between rounded-xl border border-border bg-muted/30 p-4',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-32 rounded-pill" />
          <Skeleton className="h-2.5 w-20 rounded-pill" />
        </div>
      </div>
      <div className="flex items-end justify-between">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-2.5 w-28 rounded-pill" />
          <Skeleton className="h-2.5 w-16 rounded-pill" />
        </div>
        <Skeleton className="h-16 w-16 rounded-md" />
      </div>
    </div>
  );
}

/**
 * MemberCardSkeleton — loading placeholder for the member pass page
 * (MemberCard.tsx): a heading, the wallet card itself, the two-column
 * detail list beneath it, and the row of action buttons. Mirrors the loaded
 * layout so nothing jumps when the render lands. Not a table — this is the
 * card-shaped counterpart to TableSkeleton / FormSkeleton.
 */
export function MemberCardSkeleton() {
  const { t } = useTranslation('common');
  return (
    <div role="status" aria-busy="true" aria-label={t('common.loading')}>
      <div className="mb-4 flex items-center gap-2">
        <Skeleton className="h-6 w-6 rounded-md" />
        <Skeleton className="h-6 w-48 rounded-pill" />
      </div>
      <div className="mb-4 rounded-2xl border border-border p-5">
        <CardVisualSkeleton className="mx-auto max-w-md" />
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <div key={i} className="flex flex-col gap-2">
              <Skeleton className="h-3 w-24 rounded-pill" />
              <Skeleton className="h-4 w-32 rounded-pill" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10 w-28 rounded-pill" />
        ))}
      </div>
    </div>
  );
}

/**
 * MemberCardTemplatesSkeleton — loading placeholder for the template manager
 * (MemberCardTemplates.tsx): header, a left rail of template rows, and a
 * card-preview pane on the right, matching the two-column loaded layout.
 */
export function MemberCardTemplatesSkeleton() {
  const { t } = useTranslation('common');
  return (
    <div role="status" aria-busy="true" aria-label={t('common.loading')}>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-7 w-40 rounded-pill" />
          <Skeleton className="h-4 w-64 rounded-pill" />
        </div>
        <Skeleton className="h-10 w-32 rounded-pill" />
      </div>
      <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
        <div className="flex flex-col gap-2">
          <Skeleton className="mb-1 h-3 w-24 rounded-pill" />
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-2xl border border-border p-4"
            >
              <Skeleton className="h-10 w-16 rounded-md" />
              <div className="flex flex-1 flex-col gap-2">
                <Skeleton className="h-4 w-3/4 rounded-pill" />
                <Skeleton className="h-3 w-1/3 rounded-pill" />
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-border p-5">
          <CardVisualSkeleton className="mx-auto max-w-md" />
        </div>
      </div>
    </div>
  );
}
